import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  MdToastService,
  PriceService,
  ReqService,
  SearchStateService,
} from '@core/services';
import { UsersViewsComponent } from '../users-views/users-views.component';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { environment } from '@env';
import { AppState } from '@store/app.reducer';
import { Store } from '@ngrx/store';
import { filter, takeUntil } from 'rxjs/operators';
import { PrepaidRoomCluster } from '@core/models/PrepaidRoomCluster/PrepaidRoomCluster.model';
import {
  selectAreThereMoreRooms,
  selectFilteredRooms,
  selectHotelAvailability,
} from '@store/hotel-availability/hotel-availability.selectors';
import {
  setPageSize,
  showMoreRooms,
  unsetFilters,
} from '@store/hotel-availability/hotelAvailability.actions';
import { MbBadge, MbTabs, MbTabsModule, MbExpandablePanel, MbIcon, MbButton } from '@am/moebius';
import { PriceBoxMobileComponent } from '../price-box-mobile/price-box-mobile.component';
import { RelatedHotelsComponent } from '@commons/related-hotels/related-hotels.component';
import { TripadavisorReviewsComponent } from '../tripadvisor-reviews/tripadvisor-reviews.component';
import { ServicesComponent } from '../services/services.component';
import { RateDetailComponent } from './rate-detail/rate-detail.component';
import { RoomSummaryComponent } from './room-summary/room-summary.component';
import { LoaderDetailComponent } from '../../../../commons/loader-detail/loader-detail.component';
import { RoomFiltersComponent } from '../room-filters/room-filters.component';
import { CommentsComponent } from '../comments/comments.component';
import { ParkingDisneyComponent } from '../../disney/parking/parking.component';
import { ActivitiesInformationDisneyComponent } from '../../disney/activities-information/activities-information.component';
import { CancelPoliciesComponent } from '../cancel-policies/cancel-policies.component';
import { DescriptionComponent } from '../description/description.component';
import { CheckTimesComponent } from '../check-times/check-times.component';
import { AmenitiesComponent } from '../../../../commons/amenities/amenities.component';
import { DisneyBenefitsComponent } from '../../disney/disney-benefits/disney-benefits.component';
import { AvuxiComponent } from '../../../../commons/avuxi/avuxi.component';
import { UniversalBenefitsDetailComponent } from '../universal-benefits-detail/universal-benefits-detail.component';
import { ImageMessageComponent } from '../image-message/image-message.component';
import { HeaderComponent } from './header/header.component';
import { SearchWidgetComponent } from '../../../../commons/search-widget/search-widget.component';
import { NgClass, AsyncPipe } from '@angular/common';

@Component({
    selector: 'hotel-detail-mobile',
    templateUrl: './hotel-detail-mobile.component.html',
    styleUrls: ['./hotel-detail-mobile.component.scss'],
    standalone: true,
    imports: [
        NgClass,
        MbBadge,
        SearchWidgetComponent,
        MbTabsModule,
        HeaderComponent,
        ImageMessageComponent,
        UniversalBenefitsDetailComponent,
        AvuxiComponent,
        DisneyBenefitsComponent,
        AmenitiesComponent,
        CheckTimesComponent,
        DescriptionComponent,
        CancelPoliciesComponent,
        ActivitiesInformationDisneyComponent,
        ParkingDisneyComponent,
        CommentsComponent,
        RoomFiltersComponent,
        LoaderDetailComponent,
        RoomSummaryComponent,
        MbExpandablePanel,
        RateDetailComponent,
        MbIcon,
        ServicesComponent,
        TripadavisorReviewsComponent,
        RelatedHotelsComponent,
        PriceBoxMobileComponent,
        MbButton,
        AsyncPipe,
    ],
})
export class HotelDetailMobileComponent implements OnInit, OnDestroy {
  private readonly ROOMS_PAGE_SIZE: number = 4;
  private readonly EMPTY_STATE_USD_NOT_AVAILABLE: number = 4123;
  private readonly EMPTY_STATE_CODES: number[] = [
    4121,
    4122,
    this.EMPTY_STATE_USD_NOT_AVAILABLE,
  ];

  @Input()
  isLanding: boolean;
  @Input()
  searchedEntity: any;
  @Input()
  hotelStaticInfo: any;
  @Output()
  resetState: EventEmitter<'ARS' | 'USD' | undefined> = new EventEmitter();
  @Input()
  busy: boolean;
  @Input()
  accommodationFee: any;
  @Input()
  error: boolean;

  @ViewChild('expertsCarousel')
  set expertsCarouselComponent(component: Component) {
    this.reviewsExperts = of(component);
  }

  @ViewChild('detailTabsComp') allTabs: MbTabs;

  public hotelAvailabilityInfo: any;
  public isHomestay = false;
  public detailTabs: any;
  public tabComments: boolean;
  public tabRelated: boolean;
  public selectedTab: any;
  public locale: string;
  public country: string;
  public priceByValue: string;
  public hasUniversal: boolean;
  public reviewsExperts: Observable<Component>;
  public hotelPosition: string;
  public showAvuxi: boolean;
  public isDisney: boolean;
  public bestAvailable: any;
  public selectedPaymentMethod: any;
  public refundableRateSelected: boolean;
  public showRefundableRateAlert: any;
  public showPaymentsOptionsAlert: any;
  public showChangedPricesAlert: any;
  public comments: string;
  public brand: string;
  public showBenefit = false;
  public showEmptyState = false;
  public emptyStateLabel = 'No tenemos alojamientos nacionales en dólares';
  public roomClusters$: Observable<PrepaidRoomCluster[]>;
  public areThereMoreRooms$: Observable<boolean>;
  private _recommendedHotels: any;
  private _loading$: Subject<boolean> = new BehaviorSubject(true);
  private brands: string[] = ['almundo', 'avantrip'];
  private destroy$: Subject<void> = new Subject();

  constructor(
    private searchStateService: SearchStateService,
    private priceService: PriceService,
    private reqService: ReqService,
    private mdToastService: MdToastService,
    private viewContainerRef: ViewContainerRef,
    private store: Store<AppState>,
  ) {
    this.locale = this.reqService.getLocale();
    this.roomClusters$ = this.store.select(selectFilteredRooms);
    this.areThereMoreRooms$ = this.store.select(selectAreThereMoreRooms);
  }

  @Input()
  set emptyStateCode(code: number) {
    if (code !== undefined) {
      this.showEmptyState = this.EMPTY_STATE_CODES.includes(code);
      this.emptyStateLabel =
        code === this.EMPTY_STATE_USD_NOT_AVAILABLE
          ? 'No tenemos alojamientos en dólares'
          : this.emptyStateLabel;
    }
  }

  @Input()
  set recommendedHotels(value: any[]) {
    if (value && value.length > 0) {
      this.tabRelated = true;
      this.detailTabs.push('recommended');
    }
    this._recommendedHotels = value;
  }

  get recommendedHotels(): any[] {
    return this._recommendedHotels;
  }

  ngOnInit() {
    this.brand = this.reqService.getBrand();
    this.isDisney = this.reqService.isDisneyHotel();
    this.detailTabs = ['description', 'rooms'];
    this.selectedTab = 1;
    this.country = this.reqService.getCountry();
    this.showBenefit = this.brands.includes(this.reqService.getBrand());
    this.priceByValue = this.searchStateService.getPriceBy();

    this.hotelStaticInfo = this.reqService.getHotelStaticInfo();
    this.hotelPosition = `${this.hotelStaticInfo.hotel_position.latitude},${this.hotelStaticInfo.hotel_position.longitude}`;
    this.refundableRateSelected = this.searchStateService.getFlexible();
    this.selectedPaymentMethod = this.searchStateService.getPaymentMethod();

    this.store
      .select(selectHotelAvailability)
      .pipe(
        filter(hotelAvailability => hotelAvailability !== null),
        takeUntil(this.destroy$),
      )
      .subscribe(hotelAvailability => {
        this.hotelAvailabilityInfo = hotelAvailability;

        this.hasUniversal =
          this.hotelStaticInfo.entity && this.hotelStaticInfo.entity.id === 'NEW6280590';

        this.isHomestay = this.hotelAvailabilityInfo.isHomestay;
        this.comments = this.hotelAvailabilityInfo.comments
          ? this.hotelAvailabilityInfo.comments
          : null;

        this.bestAvailable = this.getRate(true);
        this.isLanding = false;

        if (this.existsCommentsTripdavisor()) {
          this.tabComments = true;
          this.detailTabs.push('comments');
        }

        this._loading$.next(false);
      });

    this.store.dispatch(setPageSize({ pageSize: this.ROOMS_PAGE_SIZE }));
  }

  public onResetState(currencyCode?: 'ARS' | 'USD') {
    this.refundableRateSelected = !!this.searchStateService.getFlexible();
    this.store.dispatch(unsetFilters());
    this._loading$.next(true);
    this.resetState.emit(currencyCode);
  }

  public selectTab(id) {
    this.allTabs.selectTab(this.allTabs.tabs()[id], id);
    this.detailTabChange({ index: id });
  }

  /**
   * Update Detail Tab on user selection
   */
  public detailTabChange(data) {
    this.selectedTab = data.index;
    this.showAvuxi =
      (!this.reqService.isBot() && environment.production) ||
      this.searchStateService.getLocationSearch().poi;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getRate(displayAlerts?: boolean, filterAllRooms = true) {
    if (filterAllRooms) {
      this.hotelAvailabilityInfo.refundable_prepaid_room_clusters =
        this.priceService.filterRefundableRooms(
          this.hotelAvailabilityInfo.prepaid_room_clusters,
          this.isDisney,
        );
      this.hotelAvailabilityInfo.refundable_paid_at_destination_room_clusters =
        this.priceService.filterRefundableRooms(
          this.hotelAvailabilityInfo.paid_at_destination_room_clusters,
          this.isDisney,
        );
      this.hotelAvailabilityInfo.all_refundable_room_clusters =
        this.hotelAvailabilityInfo.refundable_prepaid_room_clusters.concat(
          this.hotelAvailabilityInfo.refundable_paid_at_destination_room_clusters,
        );
    }
    const paymentMethod = this.selectedPaymentMethod
      ? this.selectedPaymentMethod
      : this.searchStateService.getPaymentMethod();

    const rate = this.isDisney
      ? this.hotelAvailabilityInfo.best_disney_room_cluster
      : this.priceService.getRate(
          this.hotelAvailabilityInfo,
          paymentMethod,
          this.refundableRateSelected,
        );

    if (displayAlerts && rate !== undefined) {
      this.shouldShowChangedPricesAlert(rate);
    }
    return rate;
  }

  /**
   * Toggles refundable rates alert
   */
  private checkRefundableRateVisibility() {
    const allRooms = this.hotelAvailabilityInfo.prepaid_room_clusters.concat(
      this.hotelAvailabilityInfo.paid_at_destination_room_clusters,
    );
    let refundable = false;
    let nonrefundable = false;
    allRooms.forEach((room) => {
      if (room.rates.some((rate) => rate.refundable)) {
        refundable = true;
      }
      if (room.rates.some((rate) => !rate.refundable)) {
        nonrefundable = true;
      }
    });
    return refundable && nonrefundable;
  }

  private shouldShowChangedPricesAlert(bestAvailable: PrepaidRoomCluster): void {
    const bestAvailableRate = this.isDisney
      ? bestAvailable.disney_meal_plans[0].rates[0]
      : bestAvailable.rates[0];
    const resultPriceDifference = bestAvailableRate.price.x_difference;
    this.showChangedPricesAlert =
      resultPriceDifference > 0
        ? { value: true, type: 'EXPENSIVE' }
        : resultPriceDifference < 0
          ? { value: true, type: 'CHEAPER' }
          : { value: false, type: '' };
    this.showHotelDetailAlerts(this.showChangedPricesAlert.value);
  }

  private shouldShowRefundableRateAlert(shouldShowPaymentsOptionsAlert) {
    const refundableRateSelected = this.searchStateService.getFlexible();
    const showRefundableRateSwitch =
      !this.isDisney && this.checkRefundableRateVisibility();
    return (this.showRefundableRateAlert = {
      value:
        refundableRateSelected &&
        showRefundableRateSwitch &&
        !shouldShowPaymentsOptionsAlert,
      type: 'OTHER_OPTIONS',
    });
  }

  /**
   * Toggles Payment Methods alert
   */
  private shouldShowPaymentsOptionsAlert() {
    return (this.showPaymentsOptionsAlert =
      this.priceService.shouldShowPaymentsOptionsAlert(
        this.bestAvailable,
        this.priceByValue,
        this.hotelAvailabilityInfo,
      ));
  }

  public existsCommentsTripdavisor(): boolean {
    return this.hotelAvailabilityInfo.rating_summary?.rating != null;
  }

  private showHotelDetailAlerts(shouldShowChangedPricesAlert: boolean): void {
    this.mdToastService.setRootViewContainerRef(this.viewContainerRef);
    setTimeout(
      () => {
        const shouldShowPaymentsOptionsAlert = this.shouldShowPaymentsOptionsAlert();
        const shouldShowRefundableRateAlert = this.shouldShowRefundableRateAlert(
          shouldShowPaymentsOptionsAlert,
        );
        if (
          !shouldShowRefundableRateAlert.value &&
          !shouldShowPaymentsOptionsAlert &&
          !shouldShowChangedPricesAlert
        ) {
          const acceleratorData = {
            data: {
              nameHotel: this.hotelStaticInfo.name,
              usersViews: this.hotelStaticInfo.users_views,
            },
            hideDelay: 5000,
          };
          this.mdToastService.show(UsersViewsComponent, acceleratorData);
        }
      },
      shouldShowChangedPricesAlert ? 5000 : 2000,
    );
  }

  public showMoreRooms(): void {
    this.store.dispatch(showMoreRooms());
  }

  public get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }
}
